import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  CheckCircle2, XCircle, Loader2, Crown, ArrowRight, RefreshCw,
  Sparkles, ShieldCheck, CalendarDays, CreditCard, Home,
  RotateCcw, HeadphonesIcon, Clock, Zap, Star, Gift,
} from 'lucide-react';
import api from '../../lib/api';

/* ─── Confetti canvas ─── */
function ConfettiEffect() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#10b981', '#8b5cf6', '#f59e0b', '#3b82f6', '#ec4899', '#14b8a6', '#f97316'];
    const particles = [];

    for (let i = 0; i < 120; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        w: Math.random() * 8 + 4,
        h: Math.random() * 4 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 3,
        vy: Math.random() * 3 + 2,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        opacity: 1,
      });
    }

    let animId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particles.forEach(p => {
        if (p.opacity <= 0) return;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.04;
        p.rotation += p.rotationSpeed;
        if (p.y > canvas.height * 0.85) {
          p.opacity -= 0.02;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (alive) animId = requestAnimationFrame(animate);
    };
    animate();

    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50"
      style={{ mixBlendMode: 'multiply' }}
    />
  );
}

/* ─── Animated checkmark SVG ─── */
function AnimatedCheck() {
  return (
    <div className="relative">
      {/* Glow ring */}
      <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" />
      <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
        <svg className="h-12 w-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 13l4 4L19 7" className="animate-draw-check" style={{
            strokeDasharray: 24,
            strokeDashoffset: 24,
            animation: 'drawCheck 0.6s ease forwards 0.3s',
          }} />
        </svg>
      </div>
    </div>
  );
}

/* ─── Animated X SVG ─── */
function AnimatedX() {
  return (
    <div className="relative">
      <div className="absolute inset-0 rounded-full bg-red-400/20 animate-pulse" />
      <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30">
        <XCircle className="h-12 w-12 text-white" />
      </div>
    </div>
  );
}

/* ─── Pulsing loader ─── */
function PaymentLoader() {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const i = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="relative">
      <div className="absolute inset-0 rounded-full bg-amber-400/20 animate-ping" style={{ animationDuration: '2s' }} />
      <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
        <CreditCard className="h-10 w-10 text-white animate-pulse" />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   SUCCESS PAGE
   ════════════════════════════════════════════════════════════════ */
export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const { refreshSubscription } = useAuth();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('payment_id');
  const [status, setStatus] = useState('loading');
  const [payment, setPayment] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    if (!paymentId) { setStatus('error'); return; }
    let attempts = 0;
    const maxAttempts = 12;
    const checkStatus = async () => {
      try {
        const res = await api.get(`/payment/status/${paymentId}`);
        setPayment(res.data);
        if (res.data.status === 'confirmed') {
          setStatus('confirmed');
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000);
          if (refreshSubscription) refreshSubscription();
          return true;
        } else if (res.data.status === 'rejected' || res.data.status === 'expired') {
          setStatus('error');
          return true;
        }
        return false;
      } catch { return false; }
    };
    const poll = async () => {
      const done = await checkStatus();
      if (!done && attempts < maxAttempts) {
        attempts++;
        setTimeout(poll, 2500);
      } else if (!done) { setStatus('pending'); }
    };
    poll();
  }, [paymentId, refreshSubscription]);

  useEffect(() => {
    const t = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(t);
  }, []);

  const periodLabel = {
    1: '1 месяц', 3: '3 месяца', 6: '6 месяцев', 12: '12 месяцев',
  }[payment?.period_months] || `${payment?.period_months || ''} мес.`;

  return (
    <div className={`flex items-center justify-center min-h-[70vh] px-4 py-8 transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      {showConfetti && <ConfettiEffect />}

      {/* ── LOADING ── */}
      {status === 'loading' && (
        <Card className="max-w-lg w-full border-amber-200/50 dark:border-amber-800/30 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 animate-shimmer" style={{ backgroundSize: '200% 100%', animation: 'shimmer 2s linear infinite' }} />
          <CardContent className="p-8 sm:p-10 text-center space-y-6">
            <div className="flex justify-center">
              <PaymentLoader />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Обработка платежа
              </h2>
              <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                Ожидаем подтверждение от банка. Это займёт несколько секунд
              </p>
            </div>
            <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              <span>Безопасное соединение · Данные защищены</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── CONFIRMED ── */}
      {status === 'confirmed' && (
        <Card className="max-w-lg w-full border-emerald-300/50 dark:border-emerald-800/30 overflow-hidden shadow-xl shadow-emerald-500/5">
          <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400" />
          <CardContent className="p-8 sm:p-10 text-center space-y-7">
            <div className="flex justify-center">
              <AnimatedCheck />
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Оплата прошла успешно!
              </h1>
              <p className="text-muted-foreground text-sm">
                Спасибо за доверие! Все возможности YaDela Про теперь ваши
              </p>
            </div>

            {/* Details card */}
            <div className="rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-950/20 p-5 space-y-3 text-left">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <Crown className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-emerald-700 dark:text-emerald-300 text-sm">Тариф «Про» активирован</div>
                  <div className="text-[11px] text-emerald-600/70 dark:text-emerald-400/70">Полный доступ ко всем функциям</div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" /> Период
                  </span>
                  <span className="font-medium">{periodLabel}</span>
                </div>
                {payment?.amount_rub && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <CreditCard className="h-3.5 w-3.5" /> Оплачено
                    </span>
                    <span className="font-medium">{payment.amount_rub} ₽</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> Дата
                  </span>
                  <span className="font-medium">
                    {new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>
            </div>

            {/* Features unlocked */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Zap, text: 'Безлимитные заказы', color: 'text-amber-500' },
                { icon: Star, text: 'Полная аналитика', color: 'text-blue-500' },
                { icon: Gift, text: 'Конструктор форм', color: 'text-purple-500' },
                { icon: ShieldCheck, text: 'Приоритетная поддержка', color: 'text-emerald-500' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border/40 text-xs">
                  <f.icon className={`h-3.5 w-3.5 ${f.color} shrink-0`} />
                  <span className="text-muted-foreground">{f.text}</span>
                </div>
              ))}
            </div>

            <Button
              size="lg"
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg shadow-emerald-500/20 h-12 text-base font-medium"
              onClick={() => navigate('/')}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Начать работу
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── PENDING ── */}
      {status === 'pending' && (
        <Card className="max-w-lg w-full border-amber-200/50 dark:border-amber-800/30 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-amber-300 via-orange-300 to-amber-300 animate-pulse" />
          <CardContent className="p-8 sm:p-10 text-center space-y-6">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-amber-400/15 animate-pulse" style={{ animationDuration: '3s' }} />
                <div className="relative h-24 w-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <RefreshCw className="h-10 w-10 text-white animate-spin" style={{ animationDuration: '3s' }} />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Платёж обрабатывается</h2>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                Подтверждение от банка ещё не получено. Обычно это занимает 1–3 минуты.
                Подписка активируется автоматически — можете закрыть эту страницу.
              </p>
            </div>
            <div className="rounded-xl border border-amber-200/60 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-950/20 p-4 text-sm text-amber-700 dark:text-amber-400 flex items-start gap-3">
              <Clock className="h-5 w-5 mt-0.5 shrink-0" />
              <div className="text-left">
                <div className="font-medium">Не переживайте!</div>
                <div className="text-xs mt-0.5 opacity-80">Как только банк подтвердит платёж, вы получите уведомление на email, а подписка включится автоматически.</div>
              </div>
            </div>
            <Button
              variant="outline"
              size="lg"
              className="w-full h-11"
              onClick={() => navigate('/')}
            >
              <Home className="h-4 w-4 mr-2" />
              Вернуться на дашборд
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── ERROR ── */}
      {status === 'error' && (
        <Card className="max-w-lg w-full border-red-200/50 dark:border-red-800/30 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-red-400 via-rose-400 to-red-400" />
          <CardContent className="p-8 sm:p-10 text-center space-y-6">
            <div className="flex justify-center">
              <AnimatedX />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-red-600 dark:text-red-400">Оплата не прошла</h2>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                Платёж был отклонён или не завершён. Средства не были списаны с вашего счёта.
              </p>
            </div>
            <div className="rounded-xl border border-red-200/40 dark:border-red-800/30 bg-red-50/50 dark:bg-red-950/10 p-4 text-left">
              <div className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">Возможные причины:</div>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span>Недостаточно средств на карте</li>
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span>Банк отклонил операцию — попробуйте другую карту</li>
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span>Истекло время на ввод данных</li>
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">•</span>Платёж отменён вами</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="lg"
                className="flex-1 h-11"
                onClick={() => navigate('/support')}
              >
                <HeadphonesIcon className="h-4 w-4 mr-2" />
                Поддержка
              </Button>
              <Button
                size="lg"
                className="flex-1 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white h-11"
                onClick={() => navigate('/pricing')}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Попробовать снова
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keyframes */}
      <style>{`
        @keyframes drawCheck {
          to { stroke-dashoffset: 0; }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   FAIL PAGE
   ════════════════════════════════════════════════════════════════ */
export function PaymentFailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('payment_id');
  const [payment, setPayment] = useState(null);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (paymentId) {
      api.get(`/payment/status/${paymentId}`).then(res => setPayment(res.data)).catch(() => {});
    }
  }, [paymentId]);

  return (
    <div className={`flex items-center justify-center min-h-[70vh] px-4 py-8 transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <Card className="max-w-lg w-full border-red-200/50 dark:border-red-800/30 overflow-hidden shadow-xl shadow-red-500/5">
        <div className="h-1.5 bg-gradient-to-r from-red-400 via-rose-400 to-red-400" />
        <CardContent className="p-8 sm:p-10 text-center space-y-7">
          <div className="flex justify-center">
            <AnimatedX />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-red-600 dark:text-red-400">
              Оплата не завершена
            </h1>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Платёж был отменён или отклонён банком. Средства не списаны — попробуйте ещё раз.
            </p>
          </div>

          {/* Info about what happened */}
          {payment && (
            <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-2 text-left">
              <div className="text-xs font-medium text-muted-foreground mb-2">Детали платежа</div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="h-3.5 w-3.5" /> Период
                </span>
                <span className="font-medium">
                  {{ 1: '1 месяц', 3: '3 месяца', 6: '6 месяцев', 12: '12 месяцев' }[payment.period_months] || `${payment.period_months} мес.`}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <CreditCard className="h-3.5 w-3.5" /> Сумма
                </span>
                <span className="font-medium">{payment.amount_rub} ₽</span>
              </div>
            </div>
          )}

          {/* Possible reasons */}
          <div className="rounded-xl border border-red-200/40 dark:border-red-800/30 bg-red-50/50 dark:bg-red-950/10 p-4 text-left">
            <div className="text-xs font-medium text-red-600 dark:text-red-400 mb-2.5 flex items-center gap-1.5">
              <XCircle className="h-3.5 w-3.5" />
              Возможные причины
            </div>
            <ul className="space-y-2 text-xs text-muted-foreground">
              <li className="flex items-start gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                <span>Недостаточно средств на карте или счёте</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                <span>Банк отклонил операцию — попробуйте другую карту или способ оплаты</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                <span>Истекло время на ввод данных карты</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                <span>Платёж был отменён вами на странице оплаты</span>
              </li>
            </ul>
          </div>

          {/* Security note */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            <span>Средства не списаны · Данные карты защищены</span>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="lg"
              className="flex-1 h-12"
              onClick={() => navigate('/support')}
            >
              <HeadphonesIcon className="h-4 w-4 mr-2" />
              Поддержка
            </Button>
            <Button
              size="lg"
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg shadow-emerald-500/20 h-12"
              onClick={() => navigate('/pricing')}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Попробовать снова
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
